/**
 * Procedural sound effects (WebAudio) — pencil scratches, paper flips, thumps.
 * No audio files to download; the context is created on the first user gesture.
 *
 * Reliability rules (sounds used to drop out or clip):
 * - a sound asked for while the context is still waking up (first tap, or
 *   coming back to the tab on a phone) waits for it instead of being dropped;
 * - every sound starts a few milliseconds ahead with a tiny fade-in, so its
 *   attack is never cut and there is no click;
 * - everything goes through a limiter, so many sounds at once never distort;
 * - the same sound repeated within a few milliseconds is played once.
 */
export type SfxName = 'click' | 'flip' | 'scribble' | 'hit' | 'crit' | 'miss' | 'heal' | 'win' | 'lose' | 'lock' | 'erase' | 'pop';

/** Scheduling lookahead: starting exactly "now" cuts the first samples. */
const LEAD_S = 0.015;
/** Fade-in that removes the click of a hard start. */
const ATTACK_S = 0.004;
/** Repeats of one sound closer than this are merged. */
const MIN_GAP_MS = 35;
/** A sound waiting for the context to wake up is dropped after this (stale). */
const MAX_WAIT_MS = 800;

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private volume = 0.7;
  private muted = false;
  private lastPlayed = new Map<SfxName, number>();
  private waiting: { name: SfxName; at: number }[] = [];

  constructor() {
    const unlock = (): void => {
      if (this.ensure()) this.wake();
    };
    // iOS unlocks audio on touchend/click rather than pointerdown, so listen to all of them.
    for (const ev of ['pointerdown', 'touchend', 'click', 'keydown'] as const) window.addEventListener(ev, unlock, { passive: true, capture: true });
    // Phones suspend audio in the background; wake it as soon as the game is visible again.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.wake();
    });
    // Safari 17+: play even when the ringer switch is on silent, like a game should.
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session) {
      try {
        session.type = 'playback';
      } catch {
        /* not allowed here */
      }
    }
  }

  setVolume(volume: number, muted: boolean): void {
    this.volume = volume;
    this.muted = muted;
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(muted ? 0 : volume * 0.6, this.ctx.currentTime, 0.02);
  }

  private ensure(): boolean {
    if (this.ctx) return true;
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return false;
    this.ctx = new Ctor({ latencyHint: 'interactive' });
    this.master = this.ctx.createGain();
    this.master.gain.value = this.muted ? 0 : this.volume * 0.6;
    // Limiter: stacked hits and chords stay clean instead of clipping.
    const limiter = this.ctx.createDynamicsCompressor();
    limiter.threshold.value = -10;
    limiter.knee.value = 6;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.12;
    this.master.connect(limiter).connect(this.ctx.destination);
    const len = this.ctx.sampleRate;
    this.noise = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = this.noise.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    this.ctx.addEventListener('statechange', () => this.flushWaiting());
    return true;
  }

  /** Resumes a suspended or interrupted context (allowed after a user gesture). */
  private wake(): void {
    const ctx = this.ctx;
    if (!ctx || ctx.state === 'running' || ctx.state === 'closed') return;
    void ctx.resume().then(() => this.flushWaiting(), () => {});
  }

  private flushWaiting(): void {
    if (this.ctx?.state !== 'running') return;
    const now = performance.now();
    const queued = this.waiting.splice(0);
    for (const q of queued) if (now - q.at < MAX_WAIT_MS) this.play(q.name);
  }

  play(name: SfxName): void {
    if (this.muted) return;
    if ((!this.ctx || !this.master) && !this.ensure()) return;
    const now = performance.now();
    if (now - (this.lastPlayed.get(name) ?? -Infinity) < MIN_GAP_MS) return;
    if (this.ctx!.state !== 'running') {
      // Still waking up (first tap, back from the background): play it as soon as it can.
      if (this.waiting.length < 8) this.waiting.push({ name, at: now });
      this.wake();
      return;
    }
    this.lastPlayed.set(name, now);
    const t = this.ctx!.currentTime + LEAD_S;
    switch (name) {
      case 'click': return this.tone(520, 0.06, 'triangle', 0.25, t, 760);
      case 'pop': return this.tone(380, 0.09, 'sine', 0.35, t, 900);
      case 'lock': return this.tone(300, 0.08, 'square', 0.12, t, 200);
      case 'flip': return this.burst(0.28, 1800, 0.3, t, 'bandpass', 600);
      case 'scribble': return this.burst(0.22, 3200, 0.22, t, 'highpass');
      case 'erase': return this.burst(0.18, 900, 0.25, t, 'lowpass');
      case 'hit':
        this.tone(140, 0.18, 'sine', 0.6, t, 50);
        return this.burst(0.09, 1200, 0.3, t, 'lowpass');
      case 'crit':
        this.tone(180, 0.24, 'sawtooth', 0.3, t, 60);
        this.burst(0.12, 2500, 0.35, t, 'bandpass');
        return this.tone(880, 0.16, 'triangle', 0.2, t + 0.05, 1320);
      case 'miss': return this.tone(600, 0.2, 'sine', 0.2, t, 300);
      case 'heal':
        this.tone(523, 0.14, 'sine', 0.25, t);
        return this.tone(784, 0.2, 'sine', 0.25, t + 0.1);
      case 'win':
        [523, 659, 784, 1046].forEach((f, i) => this.tone(f, 0.2, 'triangle', 0.26, t + i * 0.11));
        return;
      case 'lose':
        [392, 330, 262].forEach((f, i) => this.tone(f, 0.27, 'triangle', 0.25, t + i * 0.16));
        return;
    }
  }

  /** Attack → exponential decay → silence, so the sound never starts or ends with a click. */
  private envelope(g: GainNode, gain: number, at: number, dur: number): void {
    g.gain.setValueAtTime(0.0001, at);
    g.gain.linearRampToValueAtTime(gain, at + ATTACK_S);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    g.gain.setValueAtTime(0, at + dur + 0.005);
  }

  private tone(freq: number, dur: number, type: OscillatorType, gain: number, at: number, endFreq?: number): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, at);
    if (endFreq) osc.frequency.exponentialRampToValueAtTime(endFreq, at + dur);
    this.envelope(g, gain, at, dur);
    osc.connect(g).connect(this.master!);
    osc.start(at);
    osc.stop(at + dur + 0.03);
    osc.onended = () => g.disconnect();
  }

  private burst(dur: number, freq: number, gain: number, at: number, type: BiquadFilterType, sweepTo?: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.setValueAtTime(freq, at);
    if (sweepTo) filter.frequency.exponentialRampToValueAtTime(sweepTo, at + dur);
    const g = ctx.createGain();
    this.envelope(g, gain, at, dur);
    src.connect(filter).connect(g).connect(this.master!);
    src.start(at, Math.random() * 0.5, dur + 0.05);
    src.onended = () => g.disconnect();
  }
}
